import { type ReactNode, type RefObject } from 'react';

import { toStageRect, type ImageRect, type Size } from '../../lib/geometry';
import { rulerLayout } from '../../lib/draw';
import type { Picture } from '../state/editor';
import { GridOverlay, type StageRulerLayout } from './GridOverlay';
import styles from './Stage.module.css';
import type { GestureHandlers } from '../hooks/useBoardGestures';
import { useFit } from '../hooks/useFit';

interface Props {
  stageRef: RefObject<HTMLDivElement | null>;
  stage: Size | null;
  picture: Picture | null;
  board: ImageRect | null;
  n: number;
  lineColor: string;
  labelScale: number;
  handlers: GestureHandlers;
  /** The empty-state block, rendered inside the stage when there is no picture. */
  children?: ReactNode;
}

/**
 * The picture well: the contain-fitted picture with the board overlay on top.
 *
 * `touch-action: none` on the stage is required, or a drag scrolls the page instead of
 * moving the board.
 */
export function Stage({
  stageRef,
  stage,
  picture,
  board,
  n,
  lineColor,
  labelScale,
  handlers,
  children,
}: Props): React.JSX.Element {
  const fit = useFit(stage, picture);
  const boardOnStage = fit !== null && board !== null ? toStageRect(board, fit) : null;

  // Ruler geometry is computed once, in image space, by the same function the export uses,
  // then scaled into stage px and made relative to the board box (ADR-0012).
  let ruler: StageRulerLayout | null = null;
  if (fit !== null && board !== null) {
    const inImage = rulerLayout(board, n, labelScale);
    ruler = {
      rf: inImage.rf * fit.s,
      columnY: (inImage.columnY - board.y) * fit.s,
      rowX: (inImage.rowX - board.x) * fit.s,
      rowsFlipped: inImage.rowsFlipped,
    };
  }

  return (
    <div
      ref={stageRef}
      className={styles.stage}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerUp}
    >
      {picture !== null && fit !== null && (
        <img
          className={styles.picture}
          src={picture.src}
          alt=""
          draggable={false}
          style={{
            left: `${fit.fitX}px`,
            top: `${fit.fitY}px`,
            width: `${fit.w}px`,
            height: `${fit.h}px`,
          }}
        />
      )}

      {boardOnStage !== null && ruler !== null && (
        <GridOverlay rect={boardOnStage} n={n} lineColor={lineColor} ruler={ruler} />
      )}

      {children}
    </div>
  );
}
